import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { apiClient } from '../../api/client';
import type { components } from '../../api/types';

export type EntitlementResponse = components['schemas']['EntitlementResponse'];
export type PackageCode = components['schemas']['PackageCode'];
export type PackageCatalogEntry = components['schemas']['PackageCatalogEntry'];

export function useEntitlements() {
  const { user, isAuthenticated } = useAuth();
  const queryClient = useQueryClient();

  const userId = user?.id;

  const query = useQuery({
    queryKey: ['entitlements', userId],
    queryFn: async (): Promise<EntitlementResponse> => {
      const { data, error } = await apiClient.GET('/api/v1/account/entitlements', {});
      if (error || !data) {
        throw error || new Error('Failed to load package entitlements');
      }
      return data;
    },
    enabled: Boolean(isAuthenticated && userId),
    staleTime: 30_000,
  });

  const retentionMutation = useMutation({
    mutationFn: async (retainedWatchIds: string[]): Promise<EntitlementResponse> => {
      const { data, error } = await apiClient.PUT(
        '/api/v1/account/entitlements/retention',
        {
          body: {
            watch_ids: retainedWatchIds,
          },
        }
      );
      if (error || !data) {
        throw error || new Error('Failed to save retained watches');
      }
      return data;
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(['entitlements', userId], updated);
      queryClient.invalidateQueries({ queryKey: ['entitlements', userId] });
    },
  });

  return {
    entitlements: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    saveRetention: retentionMutation.mutateAsync,
    isSavingRetention: retentionMutation.isPending,
    saveRetentionError: retentionMutation.error,
  };
}
